import React, { useCallback, useState, useContext, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { Controller, Control, FieldErrors } from "react-hook-form";
import styles from "./SelectDropdown.module.scss";
import block from "bem-css-modules";
import { Doctor, IFieldDropdown } from "./SelectDropdown.types";
import { sanitizeContent as s } from "../../../../util/functions";
import { AppContext } from "../../../../util/context";
import { searchDoctors } from "../../../../util/Api/api";

const b = block(styles);

const localizedContent = {
  "en-EN": {
    loadingMessage: "Loading, please wait...",
    noOptionsMessage: "No matching results found",
  },
  "de-DE": {
    loadingMessage: "Laden, bitte warten...",
    noOptionsMessage: "Keine passenden Ergebnisse gefunden",
  },
  "fr-FR": {
    loadingMessage: "Chargement, veuillez patienter...",
    noOptionsMessage: "Aucun résultat correspondant trouvé",
  },
};

const SEARCH_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 350;

const CustomOption = (props: any) => {
  const { data, innerRef, innerProps, isFocused } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      style={{
        padding: "10px",
        backgroundColor: isFocused ? "#f0f0f0" : "transparent",
        cursor: "pointer",
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: data.label }} />
    </div>
  );
};

const CustomSingleValue = (props: any) => {
  const { data } = props;
  return <div dangerouslySetInnerHTML={{ __html: data.label }} />;
};

const formatDoctorLabel = (doctor: Doctor) =>
  `<b>${s(doctor?.full_name ?? "")}</b> - ${s(doctor?.address ?? "")}`;

const toOptions = (doctors: Doctor[]) =>
  doctors.map((doctor) => ({ label: formatDoctorLabel(doctor), value: doctor }));

interface VirtualizedAsyncSelectProps {
  fieldData: IFieldDropdown;
  baseFilter: Record<string, any>;
  isManualInput: boolean;
  onStateChangeManualInput: (newState: boolean) => void;
  control: Control;
  errors: FieldErrors;
}

const VirtualizedAsyncSelect: React.FC<VirtualizedAsyncSelectProps> = ({ fieldData, baseFilter, isManualInput, onStateChangeManualInput, control, errors }) => {
  const {
    title,
    label,
    placeholder,
    validationText,
    settings: [{ required }],
  } = fieldData;

  const { settings: { locale } }: { settings: { locale: keyof typeof localizedContent } } = useContext(AppContext);

  const [loadingMessage, setLoadingMessage] = useState(localizedContent["en-EN"].loadingMessage);
  const [noOptionsMessage, setNoOptionsMessage] = useState(localizedContent["en-EN"].noOptionsMessage);
  const [defaultOptions, setDefaultOptions] = useState<any[]>([]);

  const baseFilterRef = useRef(baseFilter);
  baseFilterRef.current = baseFilter;

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    if (locale && localizedContent[locale]) {
      setLoadingMessage(localizedContent[locale].loadingMessage);
      setNoOptionsMessage(localizedContent[locale].noOptionsMessage);
    }
  }, [locale]);

  const baseFilterKey = JSON.stringify(baseFilter);
  useEffect(() => {
    let cancelled = false;
    searchDoctors("", baseFilter, SEARCH_LIMIT, 0)
      .then(({ items }) => {
        if (!cancelled) setDefaultOptions(toOptions(items));
      })
      .catch(() => {
        if (!cancelled) setDefaultOptions([]);
      });
    return () => { cancelled = true; };
  }, [baseFilterKey]);

  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  const loadOptions = useCallback(
    (inputValue: string, callback: (opts: any[]) => void) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (!inputValue) {
        callback(defaultOptions);
        return;
      }
      const seq = ++requestSeq.current;
      debounceTimer.current = setTimeout(() => {
        searchDoctors(inputValue, baseFilterRef.current, SEARCH_LIMIT, 0)
          .then(({ items }) => {
            if (seq !== requestSeq.current) return;
            callback(toOptions(items));
          })
          .catch(() => {
            if (seq !== requestSeq.current) return;
            callback([]);
          });
      }, SEARCH_DEBOUNCE_MS);
    },
    [defaultOptions]
  );

  return (
    <div className={b()}>
      {title && (
        <h3 className={b("title")} dangerouslySetInnerHTML={{ __html: s(title) }} />
      )}

      <Controller
        name={label}
        control={control}
        rules={{ required: isManualInput ? false : required }}
        render={({ field }) => {
          useEffect(() => {
            if (isManualInput) {
              field.onChange(null);
            }
          }, [field, isManualInput]);

          const selectedOption = field.value
            ? { label: formatDoctorLabel(field.value), value: field.value }
            : null;

          return (
            <AsyncSelect
              key={baseFilterKey}
              cacheOptions
              defaultOptions={defaultOptions}
              loadOptions={loadOptions}
              components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
              placeholder={placeholder}
              className={b("select")}
              classNamePrefix="react-select"
              onChange={(val: any) => {
                field.onChange(val?.value);
                if (val) {
                  onStateChangeManualInput(false);
                }
              }}
              value={selectedOption}
              loadingMessage={() => loadingMessage}
              noOptionsMessage={() => noOptionsMessage}
            />
          );
        }}
      />

      {errors[label] && (
        <div className={b("error")} dangerouslySetInnerHTML={{ __html: s(validationText) }} />
      )}
    </div>
  );
};

export default VirtualizedAsyncSelect;
